<template>
  <section data-testid="feed-preparation-passthrough-panel">
    <p
      class="text-body-2 text-medium-emphasis mb-4"
      data-testid="feed-preparation-passthrough-description"
    >
      {{ DESCRIPTION }}
    </p>

    <!-- Server-side VIN search across ALL pass-through VINs (not just loaded rows). -->
    <div class="d-flex align-center ga-3 flex-wrap mb-3">
      <v-text-field
        v-model="searchInput"
        label="Search VIN"
        variant="outlined"
        density="comfortable"
        hide-details
        clearable
        :maxlength="VIN_MAX_LENGTH"
        autocomplete="off"
        :disabled="searchStatus === 'loading'"
        data-testid="feed-preparation-passthrough-search-input"
        @keydown.enter.prevent="submitSearch"
        @click:clear="clearSearch"
      />
      <v-btn
        color="primary"
        variant="flat"
        :loading="searchStatus === 'loading'"
        :disabled="searchSubmitDisabled"
        data-testid="feed-preparation-passthrough-search-submit"
        @click="submitSearch"
      >
        Search
      </v-btn>
    </div>

    <h3 class="text-subtitle-1 font-weight-bold mb-3" data-testid="feed-preparation-passthrough-heading">
      {{ heading }}
    </h3>

    <!-- Initial load. -->
    <div
      v-if="browseStatus === 'loading' && records.length === 0"
      class="d-flex align-center ga-2"
      data-testid="feed-preparation-passthrough-loading"
    >
      <v-progress-circular indeterminate color="primary" size="20" />
      <span class="text-body-2">Loading results...</span>
    </div>

    <v-alert
      v-else-if="browseStatus === 'unavailable' && records.length === 0"
      type="info"
      variant="tonal"
      density="comfortable"
      data-testid="feed-preparation-passthrough-unavailable"
    >
      We couldn't load the results for this run right now.
    </v-alert>

    <!-- Search active: dedicated result / no-result panel. -->
    <template v-if="searchStatus === 'active'">
      <v-alert
        v-if="!searchMatched"
        type="info"
        variant="tonal"
        density="comfortable"
      data-testid="feed-preparation-passthrough-search-empty"
    >
      No VIN found in the vehicles that passed Feed Preparation.
    </v-alert>
      <div v-else data-testid="feed-preparation-passthrough-search-result">
        <v-table density="compact" class="border rounded">
          <thead>
            <tr>
              <th>VIN</th>
              <th v-if="searchRow?.feedSourcesLine">Source feeds</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr :data-testid="`feed-preparation-passthrough-row-${searchVin}`">
              <td><code class="vin-cell">{{ searchVin }}</code></td>
              <td v-if="searchRow?.feedSourcesLine">{{ searchRow.feedSourcesLine }}</td>
              <td>{{ RESULT_LABEL }}</td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </template>

    <v-alert
      v-else-if="searchStatus === 'unavailable'"
      type="info"
      variant="tonal"
      density="comfortable"
      data-testid="feed-preparation-passthrough-search-unavailable"
    >
      We couldn't search vehicles right now. Please try again.
    </v-alert>

    <!-- Browse table. -->
    <template v-else-if="records.length > 0">
      <div class="passthrough-results-scroll">
        <v-table density="compact" class="border rounded" data-testid="feed-preparation-passthrough-table">
          <thead>
            <tr>
              <th>VIN</th>
              <th v-if="hasBrowseFeedSources">Source feeds</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.key"
              :data-testid="`feed-preparation-passthrough-row-${row.vin}`"
            >
              <td><code class="vin-cell">{{ row.vin }}</code></td>
              <td v-if="hasBrowseFeedSources">{{ row.feedSourcesLine }}</td>
              <td>{{ RESULT_LABEL }}</td>
            </tr>
          </tbody>
        </v-table>
      </div>

      <div class="d-flex align-center justify-center mt-3 ga-3 flex-wrap">
        <v-btn
          v-if="hasMore"
          size="small"
          variant="tonal"
          color="primary"
          :loading="browseStatus === 'loading'"
          :disabled="browseStatus === 'loading'"
          data-testid="feed-preparation-passthrough-load-more"
          @click="loadMore"
        >
          Load more
        </v-btn>
      </div>

      <v-alert
        v-if="browseStatus === 'unavailable'"
        type="info"
        variant="tonal"
        density="comfortable"
        class="mt-3"
        data-testid="feed-preparation-passthrough-load-failed"
      >
        We couldn't load more results right now.
      </v-alert>
    </template>

    <!-- OK but empty. -->
    <v-alert
      v-else-if="browseStatus === 'ok'"
      type="info"
      variant="tonal"
      density="comfortable"
      data-testid="feed-preparation-passthrough-empty"
    >
      No vehicles passed Feed Preparation for this run.
    </v-alert>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import {
  getPublishFlowPassThroughDecisions,
  searchPublishFlowPassThroughVin,
} from '@/services/publishFlowDecisionService'
import type { PublishDecisionRecord } from '@/services/publishPreviewArtifactsService'
import { formatFeedSource } from '@/utils/feedSourceFormat'

/**
 * Feed Preparation pass-through detail panel (direct-account / no-preProcessor
 * runs). Self-contained so the already-large detailed-impact dialog stays
 * focused: it owns its own paged browse + server-side VIN search against the
 * `?mode=passThrough` API. No tabs, no raw backend stage keys — VINs that
 * "passed Feed Preparation".
 */

const props = defineProps<{
  endpoint: string
  jazelId: string
  correlationId: string
  sessionToken: string
  /** True while this panel is the visible dialog body. Drives first-page load. */
  active: boolean
}>()

const DESCRIPTION =
  'No Pre-processor filters were configured for this run. These source VINs passed through unchanged.'
const RESULT_LABEL = 'Passed Feed Preparation'
const VIN_MAX_LENGTH = 64
const PAGE_SIZE = 50

type BrowseStatus = 'idle' | 'loading' | 'ok' | 'unavailable'
type SearchStatus = 'idle' | 'loading' | 'active' | 'unavailable'

const records = ref<PublishDecisionRecord[]>([])
const totalRecords = ref(0)
const nextCursor = ref<string | undefined>(undefined)
const browseStatus = ref<BrowseStatus>('idle')

const searchInput = ref('')
const searchStatus = ref<SearchStatus>('idle')
const searchMatched = ref(false)
const searchVin = ref('')
const searchRecord = ref<PublishDecisionRecord | null>(null)

const hasMore = computed(() => typeof nextCursor.value === 'string' && nextCursor.value.length > 0)
const loadedCount = computed(() => records.value.length)

function formatFeedSourcesLine(raw: unknown): string {
  if (!Array.isArray(raw)) return ''
  const feeds = raw
    .filter((feed): feed is string => typeof feed === 'string' && feed.trim().length > 0)
    .map((feed) => feed.trim())
  const uniqueFeeds = Array.from(new Set(feeds))
  return uniqueFeeds.map(formatFeedSource).join(', ')
}

const rows = computed(() =>
  records.value.map((record, idx) => ({
    key: `${String(record.vin ?? '')}:${idx}`,
    vin: String(record.vin ?? ''),
    feedSourcesLine: formatFeedSourcesLine(record.feedSources),
  })),
)

const hasBrowseFeedSources = computed(() => rows.value.some((row) => row.feedSourcesLine.length > 0))

const searchRow = computed(() =>
  searchRecord.value
    ? {
        vin: String(searchRecord.value.vin ?? searchVin.value),
        feedSourcesLine: formatFeedSourcesLine(searchRecord.value.feedSources),
      }
    : null,
)

const heading = computed(() => {
  if (searchStatus.value === 'active') return 'Search result'
  if (totalRecords.value > 0) {
    // Pass-through records are deduped (per-feed + cross-feed), so the total is
    // a count of UNIQUE VINs — say so explicitly to reconcile with the Feed
    // Preparation card's "N unique VINs passed Feed Preparation" line.
    return `Showing ${loadedCount.value} of ${totalRecords.value} unique VINs`
  }
  return 'Vehicles that passed Feed Preparation'
})

const searchSubmitDisabled = computed(
  () => searchStatus.value === 'loading' || searchInput.value.trim().length === 0,
)

function normalizeVin(raw: string): string {
  return raw.trim().toUpperCase()
}

async function loadFirstPage() {
  if (browseStatus.value !== 'idle' || records.value.length > 0) return
  browseStatus.value = 'loading'
  const result = await getPublishFlowPassThroughDecisions({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
    pageSize: PAGE_SIZE,
  })
  if (result.status === 'ok') {
    records.value = Array.isArray(result.data.records) ? result.data.records : []
    totalRecords.value = result.data.totalRecords ?? records.value.length
    nextCursor.value = result.data.nextCursor
    browseStatus.value = 'ok'
    return
  }
  browseStatus.value = 'unavailable'
}

async function loadMore() {
  if (browseStatus.value === 'loading') return
  const cursor = nextCursor.value
  if (typeof cursor !== 'string' || cursor.length === 0) return
  browseStatus.value = 'loading'
  const result = await getPublishFlowPassThroughDecisions({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
    pageSize: PAGE_SIZE,
    cursor,
  })
  if (result.status === 'ok') {
    records.value = [...records.value, ...(Array.isArray(result.data.records) ? result.data.records : [])]
    totalRecords.value = result.data.totalRecords ?? totalRecords.value
    nextCursor.value = result.data.nextCursor
    browseStatus.value = 'ok'
    return
  }
  browseStatus.value = 'unavailable'
}

async function submitSearch() {
  const vin = normalizeVin(searchInput.value)
  if (vin.length === 0) {
    clearSearch()
    return
  }
  searchStatus.value = 'loading'
  searchRecord.value = null
  const result = await searchPublishFlowPassThroughVin({
    endpoint: props.endpoint,
    jazelId: props.jazelId,
    correlationId: props.correlationId,
    sessionToken: props.sessionToken,
    vin,
  })
  if (result.status === 'ok') {
    const matchedRecord = Array.isArray(result.data.records) ? result.data.records[0] : undefined
    searchVin.value = result.data.search?.vin ?? String(matchedRecord?.vin ?? vin)
    searchRecord.value = matchedRecord ?? null
    searchMatched.value = Boolean(result.data.search?.matched || matchedRecord)
    searchStatus.value = 'active'
    return
  }
  searchStatus.value = 'unavailable'
}

function clearSearch() {
  searchInput.value = ''
  searchVin.value = ''
  searchMatched.value = false
  searchRecord.value = null
  searchStatus.value = 'idle'
}

watch(
  () => props.active,
  (active) => {
    if (active) void loadFirstPage()
  },
  { immediate: true },
)
</script>

<style scoped>
.passthrough-results-scroll {
  max-height: 420px;
  overflow-y: auto;
  border-radius: 4px;
}
.vin-cell {
  font-family: 'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono',
    'Courier New', monospace;
  font-size: 12px;
}
</style>
